import type { GameDefinition, Outcome, Rng, Seat, Viewer } from '@campfire/engine';
import { nextSeat, ringStep, spreadSides } from '@campfire/track';

/** The ring has 60 squares: four sides of 15. Side k starts at square 15k (clockwise). */
export const SIDE_LEN = 15;
export const RING = 60;
/** Pawn position codes: START, a ring square 0..59, a safety square SAFE0..SAFE0+4, or HOME. */
export const START = -1;
export const SAFE0 = 100;
export const SAFE_LEN = 5;
export const HOME = 200;
/** Value used for the Sorry! card in the deck. */
export const SORRY_CARD = 0;
export const CARD_COUNTS: Readonly<Record<number, number>> = { 0: 4, 1: 5, 2: 4, 3: 4, 4: 4, 5: 4, 7: 4, 8: 4, 10: 4, 11: 4, 12: 4 };

/** Square a pawn lands on when it leaves start (end of the first slide on its own side). */
export const exitSquare = (side: number): number => side * SIDE_LEN + 4;
/** Square where a pawn turns into its safety zone (second square of its own side). */
export const entrySquare = (side: number): number => side * SIDE_LEN + 2;

export interface Slide {
  readonly side: number;
  readonly start: number;
  readonly end: number;
}
/** Two slides per side: 4 long starting at the side's 2nd square, 5 long starting at its 10th. */
export const SLIDES: readonly Slide[] = Array.from({ length: 4 }, (_, side) => [
  { side, start: side * SIDE_LEN + 1, end: side * SIDE_LEN + 4 },
  { side, start: side * SIDE_LEN + 9, end: side * SIDE_LEN + 13 },
]).flat();
const slideStartingAt = (sq: number): Slide | undefined => SLIDES.find((s) => s.start === sq);

export interface SorryOptions {
  /** Pawns each player must bring home (3 = shorter game). */
  readonly pawns: number;
  /** Drawing a 2 earns another card after you move. */
  readonly drawAgain: boolean;
}

export interface SorryStep {
  readonly seat: Seat;
  readonly pawn: number;
  readonly from: number;
  readonly to: number;
}
export interface SorryBump {
  readonly seat: Seat;
  readonly pawn: number;
}

export type SorryAction =
  | { readonly type: 'draw' }
  | { readonly type: 'pass' }
  | { readonly type: 'move'; readonly pawn: number; readonly by: number }
  | { readonly type: 'split'; readonly pawn: number; readonly by: number; readonly pawn2: number; readonly by2: number }
  | { readonly type: 'swap'; readonly pawn: number; readonly seat: Seat; readonly opp: number }
  | { readonly type: 'sorry'; readonly pawn: number; readonly seat: Seat; readonly opp: number };

export interface SorryEvent {
  readonly seat: Seat;
  readonly card: number;
  readonly kind: 'drew' | 'moved' | 'forfeit' | 'passed';
  readonly steps: readonly SorryStep[];
  readonly bumps: readonly SorryBump[];
  readonly slid: boolean;
  readonly again: boolean;
}

export interface SorryState {
  readonly options: SorryOptions;
  /** Board side used by each seat. */
  readonly sides: readonly number[];
  readonly pawns: readonly (readonly number[])[];
  readonly deck: readonly number[];
  readonly discard: readonly number[];
  readonly turn: Seat;
  readonly phase: 'draw' | 'move';
  readonly card: number | null;
  readonly last: SorryEvent | null;
  readonly result: Outcome | null;
}

/** A legal move as shown to the player. */
export interface SorryMove {
  readonly action: SorryAction;
  readonly steps: readonly SorryStep[];
  readonly bumps: readonly SorryBump[];
  readonly slid: boolean;
}

export interface SorryView {
  readonly you: Seat | null;
  readonly sides: readonly number[];
  readonly pawns: readonly (readonly number[])[];
  readonly turn: Seat;
  readonly phase: 'draw' | 'move';
  readonly card: number | null;
  readonly deckCount: number;
  readonly last: SorryEvent | null;
  readonly result: Outcome | null;
  readonly drawAgain: boolean;
  /** Legal moves for the player whose turn it is (empty for everyone else and before drawing). */
  readonly legal: readonly SorryMove[];
  /** The player may skip instead (an 11 whose only moves are swaps). */
  readonly canPass: boolean;
}

type Pawns = readonly (readonly number[])[];
interface Cand extends SorryMove {
  readonly pawns: Pawns;
}

const isTrack = (p: number): boolean => p >= 0 && p < RING;

/** One step forward for a pawn of `side`, or null if it cannot go (start, home). */
function forward1(side: number, pos: number): number | null {
  if (pos === START || pos === HOME) return null;
  if (pos >= SAFE0) return pos === SAFE0 + SAFE_LEN - 1 ? HOME : pos + 1;
  return pos === entrySquare(side) ? SAFE0 : ringStep(pos, 1, RING);
}
function backward1(side: number, pos: number): number | null {
  if (pos === START || pos === HOME) return null;
  if (pos >= SAFE0) return pos === SAFE0 ? entrySquare(side) : pos - 1;
  return ringStep(pos, -1, RING);
}
/** Where a pawn ends after `n` steps (negative = backward); null if it would run past home. */
export function walk(side: number, pos: number, n: number): number | null {
  let p: number | null = pos;
  for (let i = 0; i < Math.abs(n) && p !== null; i++) p = n > 0 ? forward1(side, p) : backward1(side, p);
  return p;
}

const setPawn = (pawns: Pawns, seat: Seat, pawn: number, pos: number): number[][] =>
  pawns.map((row, s) => (s === seat ? row.map((p, i) => (i === pawn ? pos : p)) : [...row]));

/** If the pawn stands on another colour's slide start it slides, bumping everything on the slide. */
function slide(pawns: number[][], sides: readonly number[], seat: Seat, pawn: number, bumps: SorryBump[]): boolean {
  const pos = (pawns[seat] as number[])[pawn] as number;
  const sl = isTrack(pos) ? slideStartingAt(pos) : undefined;
  if (!sl || sl.side === sides[seat]) return false;
  (pawns[seat] as number[])[pawn] = sl.end;
  pawns.forEach((row, s) =>
    row.forEach((p, i) => {
      if ((s === seat && i === pawn) || !isTrack(p) || p <= sl.start || p > sl.end) return;
      row[i] = START;
      bumps.push({ seat: s, pawn: i });
    }),
  );
  return true;
}

/** Put a pawn on `dest`: bump any opponent there, then slide if that square starts someone else's slide. */
function land(pawns: Pawns, sides: readonly number[], seat: Seat, pawn: number, dest: number): { pawns: number[][]; bumps: SorryBump[]; slid: boolean } {
  const next = setPawn(pawns, seat, pawn, dest);
  const bumps: SorryBump[] = [];
  if (isTrack(dest)) {
    next.forEach((row, s) => {
      if (s === seat) return;
      row.forEach((p, i) => {
        if (p === dest) {
          row[i] = START;
          bumps.push({ seat: s, pawn: i });
        }
      });
    });
  }
  const slid = slide(next, sides, seat, pawn, bumps);
  return { pawns: next, bumps, slid };
}

const ownAt = (pawns: Pawns, seat: Seat, pawn: number, dest: number): boolean =>
  dest !== HOME && (pawns[seat] as readonly number[]).some((p, i) => i !== pawn && p === dest);

/** Move a pawn already on the board `n` steps (negative = backward). */
function tryMove(pawns: Pawns, sides: readonly number[], seat: Seat, pawn: number, n: number) {
  const pos = (pawns[seat] as readonly number[])[pawn] as number;
  const side = sides[seat] as number;
  const dest = walk(side, pos, n);
  if (dest === null || dest === pos || ownAt(pawns, seat, pawn, dest)) return null;
  return land(pawns, sides, seat, pawn, dest);
}

function tryLeave(pawns: Pawns, sides: readonly number[], seat: Seat, pawn: number) {
  const dest = exitSquare(sides[seat] as number);
  return ownAt(pawns, seat, pawn, dest) ? null : land(pawns, sides, seat, pawn, dest);
}

const stepsOf = (before: Pawns, after: Pawns, moved: readonly (readonly [Seat, number])[]): SorryStep[] =>
  moved.map(([seat, pawn]) => ({ seat, pawn, from: (before[seat] as readonly number[])[pawn] as number, to: (after[seat] as readonly number[])[pawn] as number }));

/** Every legal play for the drawn card (before the pass option). */
function candidates(s: SorryState): Cand[] {
  if (s.phase !== 'move' || s.card === null || s.result) return [];
  const seat = s.turn;
  const mine = s.pawns[seat] as readonly number[];
  const card = s.card;
  const out: Cand[] = [];
  const add = (action: SorryAction, r: { pawns: number[][]; bumps: SorryBump[]; slid: boolean } | null, moved: readonly (readonly [Seat, number])[]) => {
    if (r) out.push({ action, pawns: r.pawns, bumps: r.bumps, slid: r.slid, steps: stepsOf(s.pawns, r.pawns, moved) });
  };
  const onBoard = mine.flatMap((p, i) => (p !== START && p !== HOME ? [i] : []));
  const firstStart = mine.indexOf(START);
  const forwardOf = (n: number) => onBoard.forEach((i) => add({ type: 'move', pawn: i, by: n }, tryMove(s.pawns, s.sides, seat, i, n), [[seat, i]]));

  if (card === SORRY_CARD) {
    if (firstStart >= 0)
      s.pawns.forEach((row, o) => {
        if (o === seat) return;
        row.forEach((p, j) => {
          if (isTrack(p)) add({ type: 'sorry', pawn: firstStart, seat: o, opp: j }, land(s.pawns, s.sides, seat, firstStart, p), [[seat, firstStart]]);
        });
      });
    return out;
  }
  if ((card === 1 || card === 2) && firstStart >= 0) add({ type: 'move', pawn: firstStart, by: card }, tryLeave(s.pawns, s.sides, seat, firstStart), [[seat, firstStart]]);
  if (card === 4) forwardOf(-4);
  else forwardOf(card);
  if (card === 10) forwardOf(-1);
  if (card === 11) {
    onBoard.forEach((i) => {
      if (!isTrack(mine[i] as number)) return;
      s.pawns.forEach((row, o) => {
        if (o === seat) return;
        row.forEach((p, j) => {
          if (!isTrack(p)) return;
          const next = setPawn(s.pawns, seat, i, p);
          (next[o] as number[])[j] = mine[i] as number;
          const bumps: SorryBump[] = [];
          const slid1 = slide(next, s.sides, seat, i, bumps);
          const slid2 = (next[o] as number[])[j] !== START && slide(next, s.sides, o, j, bumps);
          add({ type: 'swap', pawn: i, seat: o, opp: j }, { pawns: next, bumps, slid: slid1 || slid2 }, [[seat, i], [o, j]]);
        });
      });
    });
  }
  if (card === 7) {
    for (let a = 0; a < onBoard.length; a++) {
      for (let b = a + 1; b < onBoard.length; b++) {
        const pa = onBoard[a] as number;
        const pb = onBoard[b] as number;
        for (let x = 1; x <= 6; x++) {
          const first = tryMove(s.pawns, s.sides, seat, pa, x);
          let second = first && (first.pawns[seat] as number[])[pb] !== START ? tryMove(first.pawns, s.sides, seat, pb, 7 - x) : null;
          let r = first && second ? { pawns: second.pawns, bumps: [...first.bumps, ...second.bumps], slid: first.slid || second.slid } : null;
          if (!r) {
            const f2 = tryMove(s.pawns, s.sides, seat, pb, 7 - x);
            second = f2 && (f2.pawns[seat] as number[])[pa] !== START ? tryMove(f2.pawns, s.sides, seat, pa, x) : null;
            r = f2 && second ? { pawns: second.pawns, bumps: [...f2.bumps, ...second.bumps], slid: f2.slid || second.slid } : null;
          }
          add({ type: 'split', pawn: pa, by: x, pawn2: pb, by2: 7 - x }, r, [[seat, pa], [seat, pb]]);
        }
      }
    }
  }
  return out;
}

const sameAction = (a: SorryAction, b: SorryAction): boolean => {
  const x = a as unknown as Record<string, unknown>;
  const y = b as unknown as Record<string, unknown>;
  return Object.keys({ ...x, ...y }).every((k) => x[k] === y[k]);
};

const canPass = (s: SorryState, cands: readonly Cand[]): boolean =>
  s.phase === 'move' && s.card === 11 && cands.length > 0 && cands.every((c) => c.action.type === 'swap');

function shuffled<T>(items: readonly T[], rng: Rng): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [a[i], a[j]] = [a[j] as T, a[i] as T];
  }
  return a;
}
export const freshDeck = (): number[] => Object.entries(CARD_COUNTS).flatMap(([v, n]) => Array.from({ length: n }, () => Number(v)));

const winnerOf = (s: SorryState, pawns: Pawns): Seat | null => {
  const w = pawns.findIndex((row) => row.every((p) => p === HOME));
  return w >= 0 ? w : null;
};

/** The legal plays for the player to act, with what each would do. */
export function legalMoves(s: SorryState): SorryMove[] {
  return candidates(s).map(({ action, steps, bumps, slid }) => ({ action, steps, bumps, slid }));
}

export const sorry: GameDefinition<SorryState, SorryAction, SorryView, SorryOptions> = {
  id: 'sorry',
  name: 'Sorry!',
  rules: [
    'Bring all four of your pawns from Start, once around the board and into your Home. Everyone else is trying to stop you.',
    'On your turn draw a card and play it. If you cannot make any move, your turn is forfeited. You must move if you can.',
    '1 or 2: bring a pawn out of Start onto the board, or move a pawn on the board that many spaces forward. A 2 also lets you draw again (setting).',
    '3, 5, 8 and 12: move one pawn forward. 4: move one pawn backward 4. 10: move one pawn forward 10, or backward 1.',
    '7: move one pawn forward 7, or split it between two pawns (for example 3 with one and 4 with the other). Both pawns must be able to move their exact share.',
    '11: move one pawn forward 11, or swap places with an opponent pawn on the main track. If the only moves are swaps you may skip instead.',
    'Sorry!: take a pawn from your Start and put it on an opponent pawn on the main track. That pawn goes back to its Start. With no pawn in Start or no target, you forfeit.',
    'Landing on an opponent pawn bumps it back to its Start. You can never land on your own pawn. Pawns do not block each other.',
    'Slides: land exactly on the start of a slide of another colour and you whoosh to its end, bumping every pawn on the way, even your own. Your own colour slides do nothing for you.',
    'Safety zone: each pawn turns into its own coloured safety zone near its start side. Nobody can bump you there, and you need the exact number to reach Home.',
    'Backward moves (4 and 10) stay on the main track; a pawn in its safety zone can only back up within it.',
    'Not enforced: official variants let you skip the swap on an 11 any time, and some tables let a 4 back a pawn into its safety zone. The app uses the plain rules above.',
  ],
  minSeats: 2,
  maxSeats: 4,
  defaultOptions: { pawns: 4, drawAgain: true },
  optionSpecs: [
    {
      key: 'pawns',
      label: 'Pawns each',
      help: 'Fewer pawns make a shorter game.',
      choices: [
        { value: 4, label: '4 pawns' },
        { value: 3, label: '3 pawns' },
      ],
    },
    {
      key: 'drawAgain',
      label: 'Draw again on 2',
      choices: [
        { value: true, label: 'On' },
        { value: false, label: 'Off' },
      ],
    },
  ],

  setup: ({ seats, options, rng }) => ({
    options,
    sides: spreadSides(seats),
    pawns: Array.from({ length: seats }, () => Array.from({ length: options.pawns }, () => START)),
    deck: shuffled(freshDeck(), rng),
    discard: [],
    turn: 0,
    phase: 'draw',
    card: null,
    last: null,
    result: null,
  }),

  activeSeats: (s) => (s.result ? [] : [s.turn]),

  validate(s, a, seat) {
    if (seat !== s.turn || s.result) return { ok: false, reason: 'not your turn' };
    if (a.type === 'draw') return s.phase === 'draw' ? { ok: true } : { ok: false, reason: 'play your card first' };
    if (s.phase !== 'move') return { ok: false, reason: 'draw a card first' };
    const cands = candidates(s);
    if (a.type === 'pass') return canPass(s, cands) ? { ok: true } : { ok: false, reason: 'you must move' };
    return cands.some((c) => sameAction(c.action, a)) ? { ok: true } : { ok: false, reason: 'illegal move' };
  },

  apply(s, a, { rng }) {
    const seat = s.turn;
    const n = s.pawns.length;
    if (a.type === 'draw') {
      let deck = [...s.deck];
      let discard = [...s.discard];
      if (deck.length === 0) {
        deck = shuffled(discard, rng);
        discard = [];
      }
      const card = deck.pop() as number;
      const drawn: SorryState = { ...s, deck, discard, card, phase: 'move' };
      const base: SorryEvent = { seat, card, kind: 'drew', steps: [], bumps: [], slid: false, again: false };
      if (candidates(drawn).length > 0) return { ...drawn, last: base };
      return { ...drawn, discard: [...discard, card], card: null, phase: 'draw', turn: nextSeat(seat, n), last: { ...base, kind: 'forfeit' } };
    }
    const card = s.card as number;
    const done = { ...s, discard: [...s.discard, card], card: null, phase: 'draw' as const };
    if (a.type === 'pass') return { ...done, turn: nextSeat(seat, n), last: { seat, card, kind: 'passed', steps: [], bumps: [], slid: false, again: false } };
    const c = candidates(s).find((x) => sameAction(x.action, a)) as Cand;
    const winner = winnerOf(s, c.pawns);
    const again = winner === null && card === 2 && s.options.drawAgain;
    return {
      ...done,
      pawns: c.pawns,
      turn: winner !== null || again ? seat : nextSeat(seat, n),
      last: { seat, card, kind: 'moved', steps: c.steps, bumps: c.bumps, slid: c.slid, again },
      result: winner === null ? null : { winners: [winner], draw: false, reason: `${winner} got every pawn home` },
    };
  },

  view(s, viewer: Viewer): SorryView {
    const mover = viewer.kind === 'seat' && viewer.seat === s.turn;
    const cands = mover ? candidates(s) : [];
    return {
      you: viewer.kind === 'seat' ? viewer.seat : null,
      sides: s.sides,
      pawns: s.pawns,
      turn: s.turn,
      phase: s.phase,
      card: s.card,
      deckCount: s.deck.length,
      last: s.last,
      result: s.result,
      drawAgain: s.options.drawAgain,
      legal: cands.map(({ action, steps, bumps, slid }) => ({ action, steps, bumps, slid })),
      canPass: mover && canPass(s, cands),
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (seat !== s.turn || s.result) return [];
    if (s.phase === 'draw') return [{ type: 'draw' }];
    const cands = candidates(s);
    return [...cands.map((c) => c.action), ...(canPass(s, cands) ? [{ type: 'pass' } as const] : [])];
  },
};
